/**
 * Wire protocol.
 *
 * Every frame is JSON with a `t` discriminator. Adding a feature means adding a
 * new member to one of these unions plus a handler — existing clients ignore
 * message types they do not know, so a newer server stays usable by an older
 * client. Bump PROTOCOL_VERSION only for breaking changes to existing frames.
 */

import type {
  Appearance,
  Capabilities,
  ChatMessage,
  ChatScope,
  Facing,
  Group,
  GroupKind,
  Note,
  Notification,
  OfficeMap,
  Player,
  PlayerState,
  RoomAccess,
  Task,
} from './types.js';

export const PROTOCOL_VERSION = 1;

/* -------------------------------------------------------------------------- */
/* Client -> Server                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Announce yourself.
 *
 * Carries no identity: the socket is authenticated by the session cookie the
 * browser sends with the upgrade request, so a client cannot claim to be
 * someone by asking. This is only the protocol handshake.
 */
export interface HelloMessage {
  t: 'hello';
  protocol: number;
}

export interface MoveMessage {
  t: 'move';
  x: number;
  y: number;
  facing: Facing;
  moving: boolean;
}

export interface StatusMessage {
  t: 'status';
  status: string;
}

export interface ChatSendMessage {
  t: 'chat:send';
  scope: ChatScope;
  body: string;
}

export interface ChatHistoryRequest {
  t: 'chat:history';
  channelId: string;
}

export interface NoteCreateMessage {
  t: 'note:create';
  surfaceId: string;
  title: string;
  body: string;
  color: string;
}

export interface NoteUpdateMessage {
  t: 'note:update';
  id: string;
  title?: string;
  body?: string;
  color?: string;
}

export interface NoteDeleteMessage {
  t: 'note:delete';
  id: string;
}

/**
 * Jump straight to a zone.
 *
 * The server places you and answers with a `correction`, which the client
 * already knows how to snap to — a teleport and a rejected move are the same
 * thing from the client's point of view: "you are actually here".
 */
export interface NavigateMessage {
  t: 'navigate';
  zoneId: string;
}

/** Change how your character looks. */
export interface AppearanceMessage {
  t: 'appearance';
  appearance: Appearance;
}

/** Mark everything read. Notifications are per-person, so no id is needed. */
export interface NotificationsReadMessage {
  t: 'notifications:read';
}

export interface PingMessage {
  t: 'ping';
  ts: number;
}

export type ClientMessage =
  | HelloMessage
  | MoveMessage
  | StatusMessage
  | ChatSendMessage
  | ChatHistoryRequest
  | NoteCreateMessage
  | NoteUpdateMessage
  | NoteDeleteMessage
  | NavigateMessage
  | AppearanceMessage
  | NotificationsReadMessage
  | PingMessage;

export type ClientMessageType = ClientMessage['t'];

/* -------------------------------------------------------------------------- */
/* Server -> Client                                                            */
/* -------------------------------------------------------------------------- */

export interface WelcomeMessage {
  t: 'welcome';
  protocol: number;
  you: Player;
  map: OfficeMap;
  players: Player[];
  notes: Note[];
  history: ChatMessage[];
  capabilities: Capabilities;
  /** Which rooms this viewer may enter, resolved server-side. */
  access: RoomAccess[];
  /** What you missed while you were away. */
  notifications: Notification[];
  /** Groups you may mention: the global ones plus your own tags. */
  groups: Group[];
  serverTime: number;
}

/** One new mention, pushed the moment it happens. */
export interface NotificationMessage {
  t: 'notification';
  notification: Notification;
}

/**
 * A task was created or changed.
 *
 * The whole task, not a patch: tasks are small, edits are rare compared to
 * movement, and a client that joined mid-edit still ends up with the truth.
 */
export interface TaskMessage {
  t: 'task';
  task: Task;
}

export interface TaskDeletedMessage {
  t: 'task:deleted';
  id: string;
}

/** Sent when room rules change, so locks update without a reload. */
export interface AccessMessage {
  t: 'access';
  rooms: RoomAccess[];
}

/**
 * Positions of the people you can currently see.
 *
 * Partial by default: it carries only players who actually moved, and mostly
 * only those sharing your zone. A `full` snapshot — every player, a few times
 * a minute — is the one that lets the client prune people who have gone.
 * Pruning on a partial snapshot would delete everyone outside your room.
 */
export interface SnapshotMessage {
  t: 'snapshot';
  /** Server epoch milliseconds this snapshot describes. */
  time: number;
  /** True when this lists every player, so absences are meaningful. */
  full?: boolean;
  players: PlayerState[];
}

export interface PlayerJoinMessage {
  t: 'player:join';
  player: Player;
}

export interface PlayerLeaveMessage {
  t: 'player:leave';
  id: string;
}

export interface PlayerUpdateMessage {
  t: 'player:update';
  id: string;
  name?: string;
  color?: string;
  status?: string;
  appearance?: Appearance;
  avatarVersion?: number;
  groups?: Array<{ slug: string; name: string; kind: GroupKind }>;
}

/** Server rejected a move; snap back to this position. */
export interface CorrectionMessage {
  t: 'correction';
  x: number;
  y: number;
}

export interface ChatMessageFrame {
  t: 'chat';
  message: ChatMessage;
}

export interface ChatHistoryFrame {
  t: 'chat:history';
  channelId: string;
  messages: ChatMessage[];
}

export interface NoteAddedMessage {
  t: 'note:added';
  note: Note;
}

export interface NoteChangedMessage {
  t: 'note:changed';
  note: Note;
}

export interface NoteRemovedMessage {
  t: 'note:removed';
  id: string;
  surfaceId: string;
}

export interface ErrorMessage {
  t: 'error';
  code:
    | 'bad-message'
    | 'name-required'
    | 'not-found'
    | 'too-far'
    | 'rate-limited'
    | 'unsupported'
    /** Signed in, but not allowed to do that. */
    | 'forbidden'
    /** This identity joined from somewhere else and took over the avatar. */
    | 'replaced'
    | 'internal';
  message: string;
  /** True when the server is about to close the socket. */
  fatal?: boolean;
}

export interface PongMessage {
  t: 'pong';
  ts: number;
  serverTime: number;
}

export type ServerMessage =
  | TaskMessage
  | TaskDeletedMessage
  | WelcomeMessage
  | SnapshotMessage
  | PlayerJoinMessage
  | PlayerLeaveMessage
  | PlayerUpdateMessage
  | CorrectionMessage
  | ChatMessageFrame
  | ChatHistoryFrame
  | NoteAddedMessage
  | NoteChangedMessage
  | NoteRemovedMessage
  | ErrorMessage
  | AccessMessage
  | NotificationMessage
  | PongMessage;

export type ServerMessageType = ServerMessage['t'];

/* -------------------------------------------------------------------------- */
/* Validation                                                                  */
/* -------------------------------------------------------------------------- */

const FACINGS: readonly Facing[] = ['up', 'down', 'left', 'right'];
const SCOPES: readonly ChatScope[] = ['office', 'zone', 'local'];

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const str = (v: unknown, max: number): string | null =>
  typeof v === 'string' && v.length <= max ? v : null;

const finite = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

/**
 * Parse an untrusted frame into a ClientMessage, or return null.
 *
 * The server must never trust a frame that did not come through here: this is
 * the only place raw input becomes typed.
 */
export function parseClientMessage(raw: unknown): ClientMessage | null {
  if (typeof raw !== 'string' || raw.length > 64_000) return null;

  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(value) || typeof value['t'] !== 'string') return null;

  switch (value['t']) {
    case 'hello': {
      const protocol = finite(value['protocol']);
      if (protocol === null) return null;
      return { t: 'hello', protocol };
    }
    case 'move': {
      const x = finite(value['x']);
      const y = finite(value['y']);
      const facing = value['facing'];
      if (x === null || y === null) return null;
      if (typeof facing !== 'string' || !FACINGS.includes(facing as Facing)) return null;
      return { t: 'move', x, y, facing: facing as Facing, moving: value['moving'] === true };
    }
    case 'status': {
      const status = str(value['status'], 120);
      if (status === null) return null;
      return { t: 'status', status };
    }
    case 'chat:send': {
      const body = str(value['body'], 4000);
      const scope = value['scope'];
      if (body === null) return null;
      if (typeof scope !== 'string' || !SCOPES.includes(scope as ChatScope)) return null;
      return { t: 'chat:send', scope: scope as ChatScope, body };
    }
    case 'chat:history': {
      const channelId = str(value['channelId'], 120);
      if (channelId === null) return null;
      return { t: 'chat:history', channelId };
    }
    case 'note:create': {
      const surfaceId = str(value['surfaceId'], 120);
      const title = str(value['title'], 400);
      const body = str(value['body'], 20_000);
      const color = str(value['color'], 32);
      if (surfaceId === null || title === null || body === null || color === null) return null;
      return { t: 'note:create', surfaceId, title, body, color };
    }
    case 'note:update': {
      const id = str(value['id'], 120);
      if (id === null) return null;
      const out: NoteUpdateMessage = { t: 'note:update', id };
      if (value['title'] !== undefined) {
        const title = str(value['title'], 400);
        if (title === null) return null;
        out.title = title;
      }
      if (value['body'] !== undefined) {
        const body = str(value['body'], 20_000);
        if (body === null) return null;
        out.body = body;
      }
      if (value['color'] !== undefined) {
        const color = str(value['color'], 32);
        if (color === null) return null;
        out.color = color;
      }
      return out;
    }
    case 'note:delete': {
      const id = str(value['id'], 120);
      if (id === null) return null;
      return { t: 'note:delete', id };
    }
    case 'appearance': {
      const raw = value['appearance'];
      if (raw === null || typeof raw !== 'object') return null;
      // Ranges are clamped server-side; this only checks the shape.
      return { t: 'appearance', appearance: raw as Appearance };
    }
    case 'notifications:read':
      return { t: 'notifications:read' };
    case 'navigate': {
      const zoneId = str(value['zoneId'], 120);
      if (zoneId === null) return null;
      return { t: 'navigate', zoneId };
    }
    case 'ping': {
      const ts = finite(value['ts']);
      if (ts === null) return null;
      return { t: 'ping', ts };
    }
    default:
      return null;
  }
}
